/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import DashboardWrapper from "../Layout/DashboardWrapper";
import {
  createActs,
  deleteActs,
  getActs,
  updateActs,
} from "../../services/acts";
import Alert from "../Others/Alert";
import ModalUI from "../legacy/Modal";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { inputClass, selectClass } from "@/lib/formStyles";

const formatNumber = (value = 0) =>
  new Intl.NumberFormat("fr-FR").format(value || 0);

const initialForm = {
  actsTypeName: "Consultation",
  basePrice: 0,
  description: "",
  label: "",
};

const PAGE_SIZE = 20;

const Examen = () => {
  const queryClient = useQueryClient();

  const [addAct, setAddAct] = useState(initialForm);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);

  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  // editingId === null → mode création ; sinon → édition de l'acte ciblé.
  // Détermine quel endpoint (POST /acts vs PUT /acts/{id}) appeler et
  // l'intitulé du modal.
  const [editingId, setEditingId] = useState(null);
  const isEditing = editingId != null;

  // Debounce — 300 ms (cf. pattern Patient). Reset à la page 0 quand la
  // recherche change pour éviter de rester sur une page vide.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: resultData, isLoading, isFetching } = useQuery(
    ["acts", debouncedSearch, page],
    () => getActs({ search: debouncedSearch, page, size: PAGE_SIZE }),
    { staleTime: 30000, keepPreviousData: true }
  );

  const paginated = resultData?.data || {};
  const acts = paginated.content || [];
  const totalElements = paginated.totalElements || 0;
  const totalPages = paginated.totalPages || 0;
  const currentPage = paginated.currentPage || 0;
  const hasNext = paginated.hasNext || false;
  const hasPrevious = paginated.hasPrevious || false;

  // Toast d'erreur global : `apiRequest.notifyApiError` toaste déjà tout
  // `!result.ok` côté `services/acts`. On n'écrit donc plus de
  // `toast.error(result.message)` ici — on garde seulement le contrôle
  // de flux (close modal, refetch) et le toast.success côté métier.
  const { mutate: mutateCreate, isLoading: isCreating } = useMutation(
    (payload) => createActs(payload),
    {
      onSuccess: (result) => {
        if (!result?.ok) {
          setFormErrors([
            { message: result?.message || "Échec de l'enregistrement" },
          ]);
          return;
        }
        toast.success("Acte enregistré");
        closeFormModal();
        queryClient.invalidateQueries(["acts"]);
      },
    }
  );

  const { mutate: mutateUpdate, isLoading: isUpdating } = useMutation(
    ({ id, data }) => updateActs({ id, data }),
    {
      onSuccess: (result) => {
        if (!result?.ok) {
          setFormErrors([
            { message: result?.message || "Échec de la modification" },
          ]);
          return;
        }
        toast.success("Acte modifié");
        closeFormModal();
        queryClient.invalidateQueries(["acts"]);
      },
    }
  );

  const { mutate: mutateDelete, isLoading: isDeleting } = useMutation(
    (id) => deleteActs(id),
    {
      onSuccess: (result) => {
        if (!result?.ok) {
          // Toast déjà fait par `apiRequest`. On laisse le dialog ouvert
          // pour permettre une nouvelle tentative ou un annuler.
          return;
        }
        toast.success("Acte supprimé");
        setDeleteCandidate(null);
        queryClient.invalidateQueries(["acts"]);
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddAct({ ...addAct, [name]: value });
  };

  const openCreate = () => {
    setEditingId(null);
    setAddAct(initialForm);
    setFormErrors([]);
    setOpenFormModal(true);
  };

  const openEdit = (act) => {
    setEditingId(act?.id ?? null);
    // `actsTypeName` revient du back en MAJUSCULES (CONSULTATION/EXAM)
    // mais le `<select>` n'a que les valeurs « Consultation » /
    // « Exam ». On normalise à la lecture pour que la sélection
    // s'affiche correctement, et `handleSubmit` ré-uppercase en sortie.
    const normalizeTypeName = (raw) => {
      if (!raw) return "Consultation";
      const upper = String(raw).toUpperCase();
      if (upper === "EXAM") return "Exam";
      return "Consultation";
    };
    setAddAct({
      label: act?.label || "",
      basePrice: act?.basePrice ?? 0,
      description: act?.description || "",
      actsTypeName: normalizeTypeName(act?.actsTypeName),
    });
    setFormErrors([]);
    setOpenFormModal(true);
  };

  const closeFormModal = () => {
    setOpenFormModal(false);
    setFormErrors([]);
    // Reset asynchrone pour éviter le flash visuel des champs qui se
    // vident pendant l'animation de fermeture du modal.
    setTimeout(() => {
      setEditingId(null);
      setAddAct(initialForm);
    }, 200);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...addAct,
      basePrice: parseFloat(addAct.basePrice),
      actsTypeName: addAct.actsTypeName.toUpperCase(),
    };
    if (isEditing) {
      // `updateActs({ id, data })` — l'id est passé en path, le reste
      // en body. Voir services/acts.
      mutateUpdate({ id: editingId, data: payload });
    } else {
      mutateCreate(payload);
    }
  };

  return (
    <DashboardWrapper showSpinner={isLoading || isFetching || isCreating || isUpdating || isDeleting}>
      <>
        {/* Confirmation suppression — pattern aligné sur Patient/Assureur. */}
        <ModalUI
          isOpen={deleteCandidate != null}
          onClose={() => {
            if (!isDeleting) setDeleteCandidate(null);
          }}
          styles={{ width: "min(440px, 92vw)", padding: 0 }}
        >
          <div className="flex flex-col">
            <div className="flex items-start gap-4 px-6 pt-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <i className="bx bx-trash text-2xl" />
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-[0.32em] text-rose-600 font-semibold">
                  Action irréversible
                </p>
                <h2 className="text-lg font-semibold text-slate-900 leading-tight">
                  Supprimer cet acte ?
                </h2>
                <p className="text-sm text-slate-500">
                  L&apos;acte{" "}
                  <span className="font-medium text-slate-800">
                    {deleteCandidate?.label || "—"}
                  </span>{" "}
                  sera supprimé définitivement du catalogue. Cette action ne
                  peut pas être annulée.
                </p>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button
                type="button"
                onClick={() => setDeleteCandidate(null)}
                disabled={isDeleting}
                className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  if (deleteCandidate?.id != null) {
                    mutateDelete(deleteCandidate.id);
                  }
                }}
                disabled={isDeleting || deleteCandidate?.id == null}
                className="flex-[1.4] inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-sm font-medium shadow-lg shadow-rose-600/25 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <i className="bx bx-loader-alt animate-spin text-base" />
                    <span>Suppression…</span>
                  </>
                ) : (
                  <>
                    <i className="bx bx-trash text-base" />
                    <span>Supprimer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </ModalUI>

        <ModalUI
          isOpen={openFormModal}
          onClose={closeFormModal}
          styles={{ width: "min(640px, 95vw)", padding: 0 }}
        >
          <form
            onSubmit={handleSubmit}
            className="flex flex-col max-h-[80vh]"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-5">
              <div>
                <span className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  Catalogue
                </span>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                  {isEditing ? "Modifier l'acte" : "Nouvel acte"}
                </h2>
              </div>
              <button
                type="button"
                onClick={closeFormModal}
                className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 transition-colors"
              >
                <i className="bx bx-x text-2xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {formErrors?.length > 0 &&
                formErrors.map((error, idx) => (
                  <Alert key={idx} message={error.message} />
                ))}

              <label className="block">
                <span className="block text-sm font-medium text-slate-600">
                  Nom de l&apos;acte
                </span>
                <input
                  type="text"
                  name="label"
                  value={addAct.label}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Ex. Consultation généraliste"
                />
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-sm font-medium text-slate-600">
                    Prix de base (FCFA)
                  </span>
                  <input
                    type="number"
                    name="basePrice"
                    value={addAct.basePrice}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-slate-600">
                    Type
                  </span>
                  <div className="relative">
                    <select
                      name="actsTypeName"
                      value={addAct.actsTypeName}
                      onChange={handleChange}
                      className={selectClass}
                    >
                      <option value="Consultation">Consultation</option>
                      <option value="Exam">Examen</option>
                    </select>
                    <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
                  </div>
                </label>
              </div>

              <label className="block">
                <span className="block text-sm font-medium text-slate-600">
                  Description
                </span>
                <textarea
                  name="description"
                  value={addAct.description}
                  onChange={handleChange}
                  rows={4}
                  className={cn(inputClass, "h-auto py-2.5 resize-none")}
                  placeholder="Décrivez brièvement l'acte (optionnel)"
                ></textarea>
              </label>
            </div>

            <div className="sticky bottom-0 z-10 border-t border-slate-100 bg-white px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeFormModal}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 h-10 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isCreating || isUpdating}
                className="group/cta relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-green-600 px-5 h-10 text-sm font-medium text-white shadow-lg shadow-green-600/25 transition-all duration-300 hover:bg-green-700 active:bg-green-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover/cta:translate-x-full" />
                {isCreating || isUpdating ? (
                  <>
                    <i className="bx bx-loader-alt animate-spin relative z-10 text-base" />
                    <span className="relative z-10">
                      {isEditing ? "Modification…" : "Enregistrement…"}
                    </span>
                  </>
                ) : (
                  <>
                    <i className="bx bx-check relative z-10 text-base"></i>
                    <span className="relative z-10">
                      {isEditing ? "Modifier" : "Enregistrer"}
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </ModalUI>

        <div className="space-y-8">
          {/* Hero */}
          <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-100 rounded-[28px] border border-slate-200 bg-white shadow-sm p-6 md:p-8">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="space-y-3">
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                  <i className="bx bx-clipboard text-base"></i>
                  Catalogue d&apos;actes
                </span>
                <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
                  Actes &amp; tarifs
                </h1>
                <p className="text-sm text-slate-500 max-w-xl">
                  Gérez vos consultations et examens : prix de base,
                  description et catégorie.
                </p>
              </div>

              <button
                type="button"
                onClick={openCreate}
                className="group/cta relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-green-600 px-5 h-11 text-sm font-medium text-white shadow-lg shadow-green-600/25 transition-all duration-300 hover:bg-green-700 active:bg-green-800 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover/cta:translate-x-full" />
                <i className="bx bx-plus text-base relative z-10"></i>
                <span className="relative z-10">Nouvel acte</span>
              </button>
            </div>
          </section>

          {/* Search */}
          <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-200">
            <div className="relative w-full max-w-md">
              <i className="bx bx-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un acte…"
                className={cn(inputClass, "mt-0 pl-11")}
              />
            </div>
          </section>

          {/* Table */}
          <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-300 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="px-4 py-3 text-left">Acte</th>
                    <th className="px-4 py-3 text-left">Description</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-right">Prix</th>
                    <th className="px-4 py-3 text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {acts.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-16">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 mb-3">
                            <i className="bx bx-clipboard text-2xl"></i>
                          </div>
                          <p className="text-sm font-medium text-slate-700">
                            Aucun acte enregistré
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Cliquez sur « Nouvel acte » pour commencer.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {acts.map((elt) => (
                    <tr
                      key={elt.id}
                      className="group/row border-t border-slate-100 hover:bg-emerald-50/40 transition-colors"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-slate-800">
                        {elt.label}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-500 max-w-md truncate">
                        {elt.description || (
                          <span className="text-slate-300 italic">
                            aucune description
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                          {elt.actsTypeName || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-semibold text-slate-800">
                        {formatNumber(elt.basePrice)}{" "}
                        <span className="text-xs text-slate-400 font-normal">
                          FCFA
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1 opacity-70 group-hover/row:opacity-100 transition-opacity pr-2">
                          <button
                            type="button"
                            title="Modifier"
                            onClick={() => openEdit(elt)}
                            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                          >
                            <i className="bx bx-edit-alt text-base"></i>
                          </button>
                          <button
                            type="button"
                            title="Supprimer"
                            onClick={() => setDeleteCandidate(elt)}
                            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                          >
                            <i className="bx bx-trash text-base"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalElements={totalElements}
            currentCount={acts.length}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onPageChange={setPage}
            itemLabel="acte"
            isLoading={isFetching}
          />
        </div>
      </>
    </DashboardWrapper>
  );
};

export default Examen;
